import { useCallback, useEffect, useMemo, useState } from 'react';
import { contentApi, userApi } from '../services/tivigoApi';
import { getAccountAccessState, hasExpiredResellerAccess } from '../accountAccess';

const mergeUserInfo = (current, payload) => {
  const data = payload?.data || {};
  const user = data.user || {};
  const onlineStatus = data.online_status || {};
  const storedInfo = current?.user_info || {};

  return {
    ...(current || {}),
    user,
    user_info: {
      ...storedInfo,
      ...user,
      auth: 1,
      id: user.id ?? storedInfo.id,
      username: user.username || storedInfo.username,
      password: user.password || storedInfo.password,
      status: user.enabled === 0 ? 'Disabled' : user.admin_enabled === 0 ? 'Banned' : 'Active',
      active_cons: onlineStatus.active_connections ?? storedInfo.active_cons ?? 0,
      max_connections: user.max_connections ?? storedInfo.max_connections,
      exp_date: user.exp_date ?? storedInfo.exp_date,
      is_admin: user.is_admin ?? storedInfo.is_admin ?? 0,
    },
    server_info: {
      url: 'https://tivigo.store',
      port: '80',
      https_port: '443',
      server_protocol: 'https',
      ...(current?.server_info || {}),
      ...(data.server_info || {}),
    },
    account_data: data,
  };
};

const createLoadState = () => ({ loading: false, loaded: false, error: '' });
const SUPPORT_STATS_POLL_INTERVAL = 30000;

export default function useTivigoDashboardData(activeSection, initialUserInfo) {
  const [userInfo, setUserInfo] = useState(initialUserInfo);
  const [userState, setUserState] = useState(createLoadState);
  const [resellerInfo, setResellerInfo] = useState({
    is_reseller: false,
    level: null,
    discount_rate: 0,
    alt_kullanici_sayisi: 0,
  });
  const [resellerState, setResellerState] = useState(createLoadState);
  const [subUsers, setSubUsers] = useState(null);
  const [subUsersState, setSubUsersState] = useState(createLoadState);
  const [supportTickets, setSupportTickets] = useState(null);
  const [supportState, setSupportState] = useState(createLoadState);
  const [supportStats, setSupportStats] = useState({ unread_replies: 0 });
  const [referralData, setReferralData] = useState(null);
  const [referralState, setReferralState] = useState(createLoadState);
  const [movies, setMovies] = useState(null);
  const [moviesState, setMoviesState] = useState(createLoadState);
  const [series, setSeries] = useState(null);
  const [seriesState, setSeriesState] = useState(createLoadState);
  const [liveTv, setLiveTv] = useState(null);
  const [liveTvState, setLiveTvState] = useState(createLoadState);
  const accountAccess = useMemo(() => getAccountAccessState(userInfo), [userInfo]);
  const expiredResellerAccess = hasExpiredResellerAccess(accountAccess, resellerInfo.is_reseller);

  useEffect(() => {
    setUserInfo(initialUserInfo);
  }, [initialUserInfo]);

  const refreshUser = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setUserState((current) => ({ ...current, loading: true, error: '' }));
    try {
      const payload = await userApi.getUserData();
      setUserInfo((current) => mergeUserInfo(current || initialUserInfo, payload));
      setUserState({ loading: false, loaded: true, error: '' });
      return payload;
    } catch (error) {
      setUserState({ loading: false, loaded: false, error: error.message || 'Kullanıcı bilgileri alınamadı.' });
      throw error;
    }
  }, [initialUserInfo]);

  const refreshResellerInfo = useCallback(async () => {
    setResellerState((current) => ({ ...current, loading: true, error: '' }));
    try {
      const payload = await userApi.getResellerInfo();
      const data = payload?.data || {};
      setResellerInfo({
        ...data,
        is_reseller: data.is_reseller === true || Number(data.is_reseller) === 1,
        level: data.level ?? null,
        discount_rate: Number(data.discount_rate) || 0,
        alt_kullanici_sayisi: Number(data.alt_kullanici_sayisi) || 0,
      });
      setResellerState({ loading: false, loaded: true, error: '' });
      return payload;
    } catch (error) {
      setResellerInfo({ is_reseller: false, level: null, discount_rate: 0, alt_kullanici_sayisi: 0 });
      setResellerState({ loading: false, loaded: false, error: error.message || 'Bayi bilgileri alınamadı.' });
      throw error;
    }
  }, []);

  const refreshSubUsers = useCallback(async () => {
    setSubUsersState((current) => ({ ...current, loading: true, error: '' }));
    try {
      const [payload, statsPayload] = await Promise.all([
        userApi.getResellerUsers({ page: 1, limit: 1000 }),
        userApi.getResellerStats().catch(() => null),
      ]);
      const data = payload?.data || { users: [] };
      const resellerStats = statsPayload?.data || null;
      setSubUsers({
        ...data,
        reseller_stats: resellerStats,
        earnings: resellerStats?.earnings || null,
      });
      setSubUsersState({ loading: false, loaded: true, error: '' });
      return payload;
    } catch (error) {
      setSubUsers({ users: [] });
      setSubUsersState({ loading: false, loaded: false, error: error.message || 'Alt kullanıcılar alınamadı.' });
      throw error;
    }
  }, []);

  const refreshSupportStats = useCallback(async () => {
    const payload = await userApi.getTicketStats();
    const data = payload?.data || {};
    setSupportStats((current) => ({
      ...current,
      ...data,
      unread_replies: Math.max(0, Number(data.unread_replies) || 0),
    }));
    return payload;
  }, []);

  const refreshSupport = useCallback(async () => {
    setSupportState((current) => ({ ...current, loading: true, error: '' }));
    try {
      const payload = await userApi.getTickets({ page: 1, limit: 1000 });
      const tickets = payload?.data?.tickets || [];
      setSupportTickets(tickets);
      setSupportStats((current) => ({
        ...current,
        total_tickets: Number(payload?.data?.pagination?.total) || tickets.length,
        unread_replies: tickets.reduce(
          (total, ticket) => total + Math.max(0, Number(ticket?.unread_replies) || 0),
          0
        ),
      }));
      setSupportState({ loading: false, loaded: true, error: '' });
      return payload;
    } catch (error) {
      setSupportTickets([]);
      setSupportState({ loading: false, loaded: false, error: error.message || 'Destek talepleri alınamadı.' });
      throw error;
    }
  }, []);

  const refreshReferrals = useCallback(async () => {
    setReferralState((current) => ({ ...current, loading: true, error: '' }));
    try {
      const payload = await userApi.getReferrals({ page: 1, limit: 1000 });
      setReferralData(payload?.data || { referrals: [], referral_code: '' });
      setReferralState({ loading: false, loaded: true, error: '' });
      return payload;
    } catch (error) {
      setReferralData({ referrals: [], referral_code: '' });
      setReferralState({ loading: false, loaded: false, error: error.message || 'Referans verileri alınamadı.' });
      throw error;
    }
  }, []);

  const refreshMovies = useCallback(async () => {
    setMoviesState((current) => ({ ...current, loading: true, error: '' }));
    try {
      const payload = await contentApi.getMovies();
      setMovies(payload.movies);
      setMoviesState({ loading: false, loaded: true, error: '' });
      return payload;
    } catch (error) {
      setMovies([]);
      setMoviesState({ loading: false, loaded: false, error: error.message || 'Film listesi alınamadı.' });
      throw error;
    }
  }, []);

  const refreshSeries = useCallback(async () => {
    setSeriesState((current) => ({ ...current, loading: true, error: '' }));
    try {
      const payload = await contentApi.getSeries();
      setSeries(payload.series);
      setSeriesState({ loading: false, loaded: true, error: '' });
      return payload;
    } catch (error) {
      setSeries([]);
      setSeriesState({ loading: false, loaded: false, error: error.message || 'Dizi listesi alınamadı.' });
      throw error;
    }
  }, []);

  const refreshLiveTv = useCallback(async () => {
    setLiveTvState((current) => ({ ...current, loading: true, error: '' }));
    try {
      const payload = await contentApi.getLiveTv();
      setLiveTv(payload);
      setLiveTvState({ loading: false, loaded: true, error: '' });
      return payload;
    } catch (error) {
      setLiveTv({ categories: [], channels: [] });
      setLiveTvState({ loading: false, loaded: false, error: error.message || 'Canlı TV listesi alınamadı.' });
      throw error;
    }
  }, []);

  useEffect(() => {
    refreshUser().catch(() => {});
    const interval = window.setInterval(() => refreshUser({ silent: true }).catch(() => {}), 60000);
    return () => window.clearInterval(interval);
  }, [refreshUser]);

  useEffect(() => {
    if (accountAccess.isRestricted && !expiredResellerAccess) return undefined;

    refreshSupportStats().catch(() => {});
    const interval = window.setInterval(
      () => refreshSupportStats().catch(() => {}),
      SUPPORT_STATS_POLL_INTERVAL
    );
    return () => window.clearInterval(interval);
  }, [accountAccess.isRestricted, expiredResellerAccess, refreshSupportStats]);

  useEffect(() => {
    // Süresi dolan hesabın bayi olup olmadığı da sorulur: bayi ise alt kullanıcı yönetimi açık kalır.
    if (!userState.loaded || (accountAccess.isRestricted && accountAccess.reason !== 'expired')) return undefined;

    let active = true;
    let retryTimer = null;
    const load = (attempt = 0) => {
      refreshResellerInfo().catch(() => {
        if (active && attempt < 3) retryTimer = window.setTimeout(() => load(attempt + 1), 1000);
      });
    };
    const startTimer = window.setTimeout(() => load(), 250);
    return () => {
      active = false;
      window.clearTimeout(startTimer);
      window.clearTimeout(retryTimer);
    };
  }, [accountAccess.isRestricted, accountAccess.reason, initialUserInfo, refreshResellerInfo, userState.loaded]);

  useEffect(() => {
    if (!userState.loaded || (accountAccess.isRestricted && !expiredResellerAccess)) return;

    if (activeSection === 'user-actions' && !subUsersState.loaded && !subUsersState.loading) {
      refreshSubUsers().catch(() => {});
    }
    if (activeSection === 'support' && !supportState.loaded && !supportState.loading) {
      refreshSupport().catch(() => {});
    }
    // Kalan bölümler hesabın kendi aktif süresini gerektirir.
    if (accountAccess.isRestricted) return;
    if (activeSection === 'bonus' && !referralState.loaded && !referralState.loading) {
      refreshReferrals().catch(() => {});
    }
    if (activeSection === 'movies' && !moviesState.loaded && !moviesState.loading) {
      refreshMovies().catch(() => {});
    }
    if (['overview', 'series'].includes(activeSection) && !seriesState.loaded && !seriesState.loading) {
      refreshSeries().catch(() => {});
    }
    if (activeSection === 'live' && !liveTvState.loaded && !liveTvState.loading) {
      refreshLiveTv().catch(() => {});
    }
  }, [
    activeSection,
    accountAccess.isRestricted,
    expiredResellerAccess,
    liveTvState.loaded,
    liveTvState.loading,
    moviesState.loaded,
    moviesState.loading,
    referralState.loaded,
    referralState.loading,
    seriesState.loaded,
    seriesState.loading,
    subUsersState.loaded,
    subUsersState.loading,
    supportState.loaded,
    supportState.loading,
    userState.loaded,
    refreshLiveTv,
    refreshMovies,
    refreshReferrals,
    refreshSeries,
    refreshSubUsers,
    refreshSupport,
  ]);

  const actions = useMemo(() => ({
    refreshUser,
    refreshSubUsers,
    refreshSupport,
    refreshSupportStats,
    refreshReferrals,
    refreshMovies,
    refreshSeries,
    refreshLiveTv,
    async createSubUser(payload) {
      const result = await userApi.createResellerUser(payload);
      await Promise.all([refreshSubUsers(), refreshUser({ silent: true }), refreshResellerInfo()]);
      return result;
    },
    async removeSubUser(userId) {
      const result = await userApi.removeResellerUser(userId);
      await Promise.all([refreshSubUsers(), refreshUser({ silent: true }), refreshResellerInfo()]);
      return result;
    },
    async createTicket(payload) {
      const result = await userApi.createTicket(payload);
      await refreshSupport();
      return result;
    },
    async replyTicket(payload) {
      const result = await userApi.replyTicket(payload);
      await refreshSupport();
      return result;
    },
    async editTicketReply(payload) {
      const result = await userApi.editTicketReply(payload);
      await refreshSupport();
      return result;
    },
    async setTicketStatus(payload) {
      const result = await userApi.setTicketStatus(payload);
      await refreshSupport();
      return result;
    },
    async loadTicket(ticketId) {
      const result = await userApi.getTicketDetail(ticketId);
      const detail = result?.data || result || {};
      const detailReplies = Array.isArray(detail.replies) ? detail.replies : [];
      setSupportTickets((current) => (
        Array.isArray(current)
          ? current.map((ticket) => (
              String(ticket.id) === String(ticketId)
                ? {
                    ...ticket,
                    ...(detail.ticket || {}),
                    replies: detailReplies,
                    unread_replies: 0,
                  }
                : ticket
            ))
          : current
      ));
      refreshSupportStats().catch(() => {});
      return result;
    },
    async claimReferralBonus(referredUserId) {
      const result = await userApi.claimReferralBonus(referredUserId);
      await Promise.all([refreshReferrals(), refreshUser()]);
      return result;
    },
    async updateAccount(callback, payload) {
      const result = await callback(payload);
      await refreshUser();
      return result;
    },
    async purchaseDuration(payload) {
      const result = await userApi.purchaseDuration(payload);
      await Promise.all([refreshUser(), payload?.userInfo?._targetUserId ? refreshSubUsers() : Promise.resolve()]);
      return result;
    },
  }), [
    refreshLiveTv,
    refreshMovies,
    refreshReferrals,
    refreshSeries,
    refreshSubUsers,
    refreshSupport,
    refreshSupportStats,
    refreshUser,
    refreshResellerInfo,
  ]);

  return {
    userInfo,
    userState,
    resellerInfo,
    resellerState,
    subUsers,
    subUsersState,
    supportTickets,
    supportState,
    supportStats,
    referralData,
    referralState,
    movies,
    moviesState,
    series,
    seriesState,
    liveTv,
    liveTvState,
    actions,
  };
}
